import React, {useRef, useState} from 'react';
import {HLButtonStyleType} from '../../shared/components/button/HLButton';
import {Teacher, TeacherRole, TeacherRoleNameMap} from "../../shared/types/teacher";
import {getAllTeachersFetch, switchTeacherStatusFetch} from "../../app/axios/teacherFetches";
import type {ColDef} from "ag-grid-community";
import HLPageTable, {
    HLPageTableFilterConfig,
    HLPageTableRef,
    HLPageTableRowActionButton
} from '../../shared/components/table/HLPageTable';
import CreateTeacherModal from '../../modal/teacher/CreateTeacherModal';
import UpdateTeacherModal from '../../modal/teacher/UpdateTeacherModal';
import {EnabledFilterOptions} from "../../shared/types/general";

const searchFiltersConfig: HLPageTableFilterConfig<Teacher>[] = [
    {
        fieldName: 'firstName',
        label: 'Имя',
        operator: 'like',
        type: 'input'
    },
    {
        fieldName: 'lastName',
        label: 'Фамилия',
        operator: 'like',
        type: 'input'
    },
    {
        fieldName: 'login',
        label: 'Логин',
        operator: 'like',
        type: 'input'
    },
    {
        fieldName: 'role',
        label: 'Роль',
        operator: '=',
        type: 'select',
        options: Object.entries(TeacherRoleNameMap).map(([value, label]) => ({
            value,
            label
        }))
    },
    {
        fieldName: 'enabled',
        label: 'Активность',
        operator: 'is',
        type: 'select',
        options: EnabledFilterOptions,
        defaultValue: 'true'
    }
];

const columnDefs: ColDef<Teacher>[] = [
    {
        field: 'firstName',
        headerName: 'Имя',
    },
    {
        field: 'lastName',
        headerName: 'Фамилия',
    },
    {
        field: 'login',
        headerName: 'Логин',
    },
    {
        field: 'role',
        headerName: 'Роль',
        valueFormatter: (params) => TeacherRoleNameMap[params.value as TeacherRole],
    }
]

const defaultColDef: ColDef<Teacher> = {
    sortable: false,
    filter: false,
    resizable: true,
    flex: 1,
    minWidth: 100
}

const TeachersPage: React.FC = () => {
    const tableRef = useRef<HLPageTableRef>(null);

    const [isCreateTeacherModalOpen, setIsCreateTeacherModalOpen] = useState(false);
    const [isUpdateTeacherModalOpen, setIsUpdateTeacherModalOpen] = useState(false);
    const [selectedTeacher, setSelectedTeacher] = useState<Teacher | null>(null);

    const handleSuccess = () => {
        setSelectedTeacher(null)
        setIsUpdateTeacherModalOpen(false)
        setIsCreateTeacherModalOpen(false)
        tableRef.current?.refreshData()
    };

    const actionButtonsConfig = [
        {
            label: 'Создать учителя',
            onClick: () => setIsCreateTeacherModalOpen(true),
            styleType: HLButtonStyleType.ORANGE
        }
    ];

    const rowActionButtons: HLPageTableRowActionButton[] = [
        {
            onClick: params => {
                if (params?.data) {
                    setSelectedTeacher(params?.data)
                    setIsUpdateTeacherModalOpen(true)
                }
            },
            getLabel: () => 'Изменить'
        },
        {
            onClick: async params => {
                if (params?.data?.id) {
                    try {
                        await switchTeacherStatusFetch(params.data.id);
                        params.api.refreshInfiniteCache();
                    } catch (err) {
                        console.error('Ошибка при переключении статуса учителя:', err);
                    }
                }
            },
            getLabel: params => params?.data?.enabled ? 'Деактивировать' : 'Активировать'
        }
    ];

    return (
        <>
            <HLPageTable
                ref={tableRef}
                title="Учителя"
                columns={columnDefs}
                defaultColDef={defaultColDef}
                filterConfig={searchFiltersConfig}
                actionButtons={actionButtonsConfig}
                fetchData={getAllTeachersFetch}
                rowActionButtons={rowActionButtons}
                height={window.innerHeight}
            />
            <CreateTeacherModal
                open={isCreateTeacherModalOpen}
                setOpen={setIsCreateTeacherModalOpen}
                onSuccess={handleSuccess}
            />
            <UpdateTeacherModal
                open={isUpdateTeacherModalOpen}
                setOpen={setIsUpdateTeacherModalOpen}
                teacher={selectedTeacher}
                onSuccess={handleSuccess}
            />
        </>
    );
};

export default TeachersPage;